import {Injectable} from '@angular/core';
import {Observable, forkJoin, of, EMPTY} from 'rxjs';
import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';
import {FormService} from '../../main/modules/services/form.service';
import {defaultIfEmpty, map, switchMap, tap} from 'rxjs/operators';
import {DynamicService} from './dynamic.service';
import {IDynamic} from '../../main/modules/dynamic/models/IDynamic';
import {DynamicUtilService} from '../../services/dynamic-util.service';
import {ModuleSchemaStoreService} from '../../services/module-schema-store.service';
import {IModuleSchema} from '@shared/models/moduleSchema';
import {UtcToLocalPipe} from '@shared/pipes/utc-to-local.pipe';
import {FormSchema} from '@shared/widgets/models/schema';
import {DynamicDataSource} from 'src/app/main/modules/dynamic/services/dynamic-data-source.service';
import {FormSchemaStoreService} from '../../services/form-schema-store.service';
import {isFileWidget} from '@shared/files/file-field.util';
import {isJsonWidget} from '@shared/widgets/json-widget/json-field.util';
import {isChipsWidget} from '@shared/widgets/chips-widget/chips-field.util';


@Injectable()
export class DynamicListResolver implements Resolve<string[]> {

  constructor(
    private moduleSchemaStoreService: ModuleSchemaStoreService,
    private dynamicService: DynamicService,
    private dynamicUtilService: DynamicUtilService
  ) {
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const moduleName = this.dynamicUtilService.getCurrentModuleFromUrl(state.url);
    const moduleSchema = this.moduleSchemaStoreService.get(moduleName);
    this.dynamicService.table = moduleSchema.name;
    this.dynamicService.moduleId = moduleSchema.id_number;
    return of(moduleSchema);

  }
}

@Injectable()
export class DynamicEditResolver implements Resolve<any> {


  constructor(
    private formService: FormService,
    private dynamicService: DynamicService,
    private moduleSchemaStoreService: ModuleSchemaStoreService,
    private formSchemaStoreService: FormSchemaStoreService,
    private utcToLocal: UtcToLocalPipe,
    private dynamicUtilService: DynamicUtilService,
    public dataSource: DynamicDataSource,
  ) {
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const ids = route.params.id.split(',');
    const moduleId = this.dynamicUtilService.getCurrentModuleFromEditUrl(state.url);
    const moduleSchema = this.moduleSchemaStoreService.get(moduleId);
    const nestedModuleSchema: IModuleSchema = this.moduleSchemaStoreService.get(moduleSchema?.nestedModule?.name)
    const formSchema$: Observable<FormSchema> = this.formSchemaStoreService.get(moduleSchema.formId) ? of(this.formSchemaStoreService.get(moduleSchema.formId)) : this.formService.getFormSchemaByModuleId(moduleId);
    const nestedFormSchema$ = (nestedModuleSchema && (moduleSchema.displayNestedModuleForm || moduleSchema.editNestedModuleTable)) ? this.formService.getFormSchemaByModuleId(nestedModuleSchema.id_number) : EMPTY;

    this.dynamicService.table = moduleSchema.name;
    this.dynamicService.moduleId = moduleSchema.id_number;

    // const formData$: Observable<IDynamic> = this.dynamicService.getById(id)
    const formData$: Observable<IDynamic[]> = forkJoin([...ids.map(id => this.dynamicService.getById(id))]) as Observable<IDynamic[]>;
    return forkJoin([formSchema$, formData$, nestedFormSchema$.pipe(defaultIfEmpty(null))])
      .pipe(
        switchMap(([formSchema, formData, nestedFormSchema]: [FormSchema, any, any]) => {
          this.formSchemaStoreService.set(formSchema as FormSchema, moduleSchema.formId)
          this.dynamicService.schema = formSchema as FormSchema;
          formData = formData.map(form => this.processDataFields(form, formSchema as FormSchema));

          const payload: any[] = [{
            moduleSchema,
            formSchema: formSchema || {},
            formData: formData || [],
            nestedModuleSchema,
            nestedFormSchema: nestedFormSchema || {},

          }]
          if (nestedModuleSchema) {
            this.dynamicService.table = nestedModuleSchema.name;
            this.dynamicService.moduleId = nestedModuleSchema.id_number;
            // get nested nodule data for the main entity
            return this.dynamicService.find(null, null, ids[0].toString(), null, null, null, null, nestedModuleSchema?.config?.defaultOrder?.fieldName, nestedModuleSchema?.config?.defaultOrder?.order)
              .pipe(
                map((nestedFormData) => {
                  //Identifier for the particular form nestedModule opening (or the default opening if nestedFormData.data is not empty)
                  const nestedId = route.queryParams.nestedId || nestedFormData?.data.length && nestedFormData.data[0].id;

                  payload[0].nestedDataIds = nestedFormData?.data?.map(i => i.id);

                  if (moduleSchema.displayNestedModuleForm && nestedFormData.count) {
                    payload.push({
                      moduleSchema: nestedModuleSchema,
                      formSchema: nestedFormSchema || {},
                      formData: nestedFormData,
                      nestedId,

                    })
                  }
                  return payload
                })
              );
          } else {
            return of(payload)
          }

        }),
        tap(() => {
          this.dynamicService.table = moduleSchema.name;
          this.dynamicService.moduleId = moduleSchema.id_number;
        })
      )
  }

  private processDataFields(data: any, formSchema: FormSchema): any {
    data.createdAt = this.utcToLocal.transform(data.createdAt);
    data.lastEditedAt = this.utcToLocal.transform(data.lastEditedAt);
    const fieldsToConvert = Object.entries(formSchema.properties)
      .filter(([key, value]) => value.format === 'datetime' && !['createdAt', 'lastEditedAt'].includes(key))
      .map(([key]) => key);

    fieldsToConvert.forEach(field => {
      data[field] = this.utcToLocal.transform(data[field]);
    });


    // todo move to backend and change this function name to processDateFormatting
    const multiSelectFields = Object.entries(formSchema.properties)
      .filter(([key, value]) => value.format === 'multiple' && !isFileWidget(value) && !isJsonWidget(value) && !isChipsWidget(value))
      .map(([key]) => key);

    multiSelectFields.forEach(field => {
      const value = data[field];
      data[field] = typeof value === 'string' ? value.split(',') : value;
    });

    return data;
  }
}


@Injectable()
export class DynamicNewResolver implements Resolve<any> {

  constructor(
    private formService: FormService,
    private dynamicService: DynamicService,
    private dynamicUtilService: DynamicUtilService,
    private formSchemaStoreService: FormSchemaStoreService,
    private moduleSchemaStoreService: ModuleSchemaStoreService,
  ) {
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const moduleName = this.dynamicUtilService.getCurrentModuleFromEditUrl(state.url);
    const moduleSchema = this.moduleSchemaStoreService.get(moduleName);
    this.dynamicService.schema = this.formSchemaStoreService.get(moduleSchema.formId);

    this.dynamicService.table = moduleSchema.name;
    this.dynamicService.moduleId = moduleSchema.id_number;

    if (this.dynamicService.schema) {
      return of([{formSchema: this.dynamicService.schema, formData: {}, moduleSchema, moduleName}]);
    } else {
      return this.formService.getFormSchemaByModuleId(moduleSchema.id_number).pipe(
        map(formSchema => {
          this.dynamicService.schema = formSchema
          this.formSchemaStoreService.set(formSchema, moduleSchema.formId)
          return [{formSchema, formData: {}, moduleSchema, moduleName}];
        }));
    }
  }

}



